import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Alert,
  Button,
  Card,
  Divider,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  theme,
  message,
} from 'antd'
import {
  CarOutlined,
  CheckCircleOutlined,
  CameraOutlined,
  FileImageOutlined,
  PlusOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { usePermission } from '@/hooks/usePermission'
import { useCustomers } from '@/hooks/useCustomers'
import { useVehicles, useCustomerVehicleHistory } from '@/hooks/useVehicles'
import { useCreateWorkOrder, useUpdateWorkOrder, useDeleteWorkOrder } from '@/hooks/useWorkOrders'
import CustomerFormModal from '@/components/CustomerFormModal'
import VehicleFormModal from '@/components/VehicleFormModal'
import { PhotoCaptureManager, PhotoCaptureManagerHandle } from '@/components/PhotoCaptureManager'
import VoiceTextarea from '@/components/VoiceTextarea'
import type { Customer } from '@/types'
import type { Vehicle } from '@/types'

const { Text, Title } = Typography

type Phase =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'draft'; woId: number; woNumero: string }

export default function PreAccettazionePage() {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // ── State ───────────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null)
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null)
  const [dannoText, setDannoText] = useState('')
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false)
  const [converting, setConverting] = useState(false)

  const photoCaptureRef = useRef<PhotoCaptureManagerHandle>(null)
  const dannoSavedRef = useRef('')

  const canViewCustomers = usePermission('customers.view')
  const canViewVehicles  = usePermission('vehicles.view')

  // ── Data ────────────────────────────────────────────────────────────────────
  const { data: customersData } = useCustomers(1, 500, undefined, canViewCustomers)
  const customers = customersData?.items ?? []

  const { data: vehiclesData } = useVehicles(1, 500, undefined, canViewVehicles)
  const allVehicles = vehiclesData?.items ?? []

  const { data: customerVehicles = [] } = useCustomerVehicleHistory(
    selectedCustomerId ?? undefined
  )

  const createWO = useCreateWorkOrder()
  const updateWO = useUpdateWorkOrder()
  const deleteWO = useDeleteWorkOrder()

  // ── Auto-save: crea WO bozza quando cliente + veicolo selezionati ────────────
  useEffect(() => {
    if (!selectedCustomerId || !selectedVehicleId) return
    if (phase.kind !== 'idle') return

    setPhase({ kind: 'saving' })
    createWO.mutate(
      {
        customer_id: selectedCustomerId,
        vehicle_id: selectedVehicleId,
        stato: 'bozza',
        priorita: 'media',
        valutazione_danno: dannoText.trim() || undefined,
      },
      {
        onSuccess: (wo) => {
          dannoSavedRef.current = dannoText.trim()
          setPhase({
            kind: 'draft',
            woId: wo.id,
            woNumero: wo.numero_scheda ?? `#${wo.id}`,
          })
        },
        onError: () => {
          setPhase({ kind: 'idle' })
        },
      }
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, selectedVehicleId])

  // ── Debounce auto-save descrizione danno ────────────────────────────────────
  useEffect(() => {
    if (phase.kind !== 'draft') return
    if (dannoText.trim() === dannoSavedRef.current) return

    const t = setTimeout(() => {
      updateWO.mutate(
        { id: phase.woId, data: { valutazione_danno: dannoText.trim() || undefined } },
        {
          onSuccess: () => {
            dannoSavedRef.current = dannoText.trim()
          },
        }
      )
    }, 1500)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dannoText])

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const doReset = useCallback(() => {
    photoCaptureRef.current?.discardChanges()
    setPhase({ kind: 'idle' })
    setSelectedCustomerId(null)
    setSelectedVehicleId(null)
    setDannoText('')
    dannoSavedRef.current = ''
    queryClient.invalidateQueries({ queryKey: ['work-orders'] })
  }, [queryClient])

  // Usato da cambio cliente/veicolo: cancella la bozza silenziosamente
  const resetAll = useCallback(() => {
    if (phase.kind === 'draft') {
      deleteWO.mutate(phase.woId)
    }
    doReset()
  }, [phase, doReset, deleteWO])

  // Usato dal bottone "Nuova scheda": chiede conferma prima di cancellare
  const handleNuovaScheda = useCallback(() => {
    if (phase.kind !== 'draft') {
      doReset()
      return
    }
    const woId = phase.woId
    const woNumero = phase.woNumero
    Modal.confirm({
      title: 'Eliminare la bozza?',
      content: `La scheda ${woNumero} in bozza verrà eliminata definitivamente.`,
      okText: 'Elimina e ricomincia',
      okButtonProps: { danger: true },
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await deleteWO.mutateAsync(woId)
        } catch {
          // resetta comunque anche se la delete fallisce
        }
        doReset()
      },
    })
  }, [phase, doReset, deleteWO])

  const handleConvertToAccettazione = useCallback(async () => {
    if (phase.kind !== 'draft') return
    setConverting(true)
    try {
      // Flush descrizione danno prima di convertire
      await updateWO.mutateAsync({
        id: phase.woId,
        data: { valutazione_danno: dannoText.trim() || undefined },
      })
      // Commit staged photos before navigating away
      await photoCaptureRef.current?.commitPhotos()
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      navigate(`/schede-lavoro/${phase.woId}`)
    } catch {
      message.error('Errore durante la conversione')
      setConverting(false)
    }
  }, [phase, dannoText, updateWO, queryClient, navigate])

  // ── Select options ──────────────────────────────────────────────────────────
  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: c.tipo === 'privato'
      ? `${c.cognome ?? ''} ${c.nome ?? ''}`.trim()
      : (c.ragione_sociale ?? ''),
  }))

  const customerVehicleIds = new Set(customerVehicles.map((v: Vehicle) => v.id))

  const vehicleGroupOptions = [
    ...(customerVehicles.length > 0
      ? [
          {
            label: 'Veicoli del cliente',
            options: customerVehicles.map((v: Vehicle) => ({
              value: v.id,
              label: `${v.targa} — ${v.marca ?? ''} ${v.modello ?? ''}`.trim(),
            })),
          },
        ]
      : []),
    {
      label: 'Tutti i veicoli',
      options: allVehicles
        .filter((v) => !customerVehicleIds.has(v.id))
        .map((v) => ({
          value: v.id,
          label: `${v.targa} — ${v.marca ?? ''} ${v.modello ?? ''}`.trim(),
        })),
    },
  ]

  // ── Render ──────────────────────────────────────────────────────────────────
  const woId = phase.kind === 'draft' ? phase.woId : 0

  return (
    <div
      style={{
        maxWidth: 800,
        margin: '0 auto',
        padding: '16px 16px 40px',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0, color: token.colorTextHeading }}>
          Pre-Accettazione
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Registra l'ingresso di un veicolo in modo rapido
        </Text>
      </div>

      {/* Alert bozza creata */}
      {phase.kind === 'draft' && (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          message={
            <span>
              Bozza <strong>{phase.woNumero}</strong> creata — aggiungi foto e descrizione
            </span>
          }
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}
      {phase.kind === 'saving' && (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          message="Creazione bozza in corso…"
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* ── Cliente ── */}
      <Card
        size="small"
        title={
          <Space>
            <span style={{ color: token.colorPrimary }}>1</span>
            <span>Cliente</span>
            {selectedCustomerId && (
              <Tag color="green" style={{ marginLeft: 4 }}>
                ✓
              </Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 12, borderRadius: 10 }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            showSearch
            placeholder="Cerca cliente per nome o ragione sociale…"
            style={{ flex: 1, height: 48 }}
            size="large"
            value={selectedCustomerId ?? undefined}
            onChange={(val) => {
              setSelectedCustomerId(val)
              setSelectedVehicleId(null)
              if (phase.kind !== 'idle') resetAll()
            }}
            options={customerOptions}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={
              <Text type="secondary" style={{ fontSize: 13 }}>
                Nessun cliente trovato
              </Text>
            }
            allowClear
            onClear={() => {
              setSelectedCustomerId(null)
              setSelectedVehicleId(null)
              if (phase.kind !== 'idle') resetAll()
            }}
          />
          <Button
            icon={<UserAddOutlined />}
            size="large"
            style={{ height: 48, width: 48, flexShrink: 0 }}
            title="Nuovo cliente"
            onClick={() => setCustomerModalOpen(true)}
          />
        </div>
      </Card>

      {/* ── Veicolo ── */}
      <Card
        size="small"
        title={
          <Space>
            <span style={{ color: token.colorPrimary }}>2</span>
            <span>Veicolo</span>
            {selectedVehicleId && (
              <Tag color="green" style={{ marginLeft: 4 }}>
                ✓
              </Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 12, borderRadius: 10 }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            showSearch
            placeholder={
              selectedCustomerId
                ? 'Cerca veicolo per targa o modello…'
                : 'Seleziona prima il cliente'
            }
            style={{ flex: 1, height: 48 }}
            size="large"
            disabled={!selectedCustomerId || phase.kind === 'saving'}
            value={selectedVehicleId ?? undefined}
            onChange={(val) => {
              if (phase.kind !== 'idle') resetAll()
              setSelectedVehicleId(val)
            }}
            options={vehicleGroupOptions}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={
              <Text type="secondary" style={{ fontSize: 13 }}>
                Nessun veicolo trovato
              </Text>
            }
            allowClear
            onClear={() => {
              setSelectedVehicleId(null)
              if (phase.kind !== 'idle') resetAll()
            }}
          />
          <Button
            icon={<CarOutlined />}
            size="large"
            style={{ height: 48, width: 48, flexShrink: 0 }}
            title="Nuovo veicolo"
            disabled={!selectedCustomerId}
            onClick={() => setVehicleModalOpen(true)}
          />
        </div>
      </Card>

      {/* ── Descrizione danno ── */}
      <Card
        size="small"
        title={
          <Space>
            <span style={{ color: token.colorPrimary }}>3</span>
            <span>Descrizione del danno</span>
          </Space>
        }
        style={{ marginBottom: 12, borderRadius: 10 }}
      >
        <VoiceTextarea
          value={dannoText}
          onChange={setDannoText}
          rows={3}
          minHeight={90}
          maxHeight={200}
          placeholder="Descrivi il danno o il problema segnalato dal cliente…"
          label="Descrizione danno"
          compact
        />
        {phase.kind === 'draft' && updateWO.isPending && (
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            Salvataggio in corso…
          </Text>
        )}
      </Card>

      {/* ── Foto ── */}
      <Card
        size="small"
        title={
          <Space>
            <span style={{ color: token.colorPrimary }}>4</span>
            <span>Foto del danno</span>
          </Space>
        }
        style={{ marginBottom: 20, borderRadius: 10 }}
        extra={
          phase.kind === 'draft' ? (
            <Space>
              <Button
                size="middle"
                icon={<CameraOutlined />}
                onClick={() => photoCaptureRef.current?.openWebcamModal()}
                style={{ height: 44 }}
              >
                Scatta
              </Button>
              <Button
                size="middle"
                icon={<FileImageOutlined />}
                onClick={() => photoCaptureRef.current?.openUploadModal()}
                style={{ height: 44 }}
              >
                Carica
              </Button>
            </Space>
          ) : null
        }
      >
        {phase.kind !== 'draft' ? (
          <div
            style={{
              padding: '24px 0',
              textAlign: 'center',
              color: token.colorTextSecondary,
              fontSize: 13,
            }}
          >
            Seleziona cliente e veicolo per abilitare il caricamento foto
          </div>
        ) : (
          <PhotoCaptureManager
            ref={photoCaptureRef}
            workOrderId={woId}
            onPhotoAdded={() => {}}
            compact
          />
        )}
      </Card>

      <Divider style={{ margin: '0 0 16px' }} />

      {/* ── Footer ── */}
      <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
        <Button
          type="primary"
          size="large"
          block
          icon={<CheckCircleOutlined />}
          disabled={phase.kind !== 'draft'}
          loading={converting}
          onClick={handleConvertToAccettazione}
          style={{ height: 52, fontSize: 16, borderRadius: 8 }}
        >
          Conferma in Accettazione
        </Button>
        <Button
          size="large"
          block
          icon={<ReloadOutlined />}
          onClick={handleNuovaScheda}
          style={{ height: 48, borderRadius: 8 }}
        >
          Nuova scheda
        </Button>
      </div>

      {/* ── Modali ── */}
      <CustomerFormModal
        open={customerModalOpen}
        onCancel={() => setCustomerModalOpen(false)}
        onSuccess={(customer: Customer) => {
          queryClient.invalidateQueries({ queryKey: ['customers'] })
          setSelectedCustomerId(customer.id)
          setCustomerModalOpen(false)
        }}
      />

      <VehicleFormModal
        open={vehicleModalOpen}
        onCancel={() => setVehicleModalOpen(false)}
        onSuccess={(vehicle: Vehicle) => {
          queryClient.invalidateQueries({ queryKey: ['vehicles'] })
          queryClient.invalidateQueries({
            queryKey: ['customer-vehicle-history', selectedCustomerId],
          })
          setSelectedVehicleId(vehicle.id)
          setVehicleModalOpen(false)
        }}
      />
    </div>
  )
}
